import dayjs, { Dayjs } from "dayjs";

export const emptySpaceErr = "Only spaces are not allowed";

export type DateRangeType = "today" | "week" | "month" | "six_months" | "year";

export function getDateRange(rangeType: DateRangeType): [Dayjs, Dayjs] {
  const today = dayjs();

  switch (rangeType) {
    case "today":
      return [today.startOf("day"), today.endOf("day")];

    case "week":
      return [today.startOf("week"), today.endOf("week")];

    case "month":
      return [today.startOf("month"), today.endOf("month")];

    case "six_months":
      return [
        today.subtract(6, "month").startOf("month"),
        today.endOf("month"),
      ];

    case "year":
      return [today.startOf("year"), today.endOf("year")];

    default:
      throw new Error(`Unsupported range type: ${rangeType}`);
  }
}

export function getSum(numbers: number[]) {
  return numbers.reduce((acc: number, num: number) => acc + num, 0);
}

export const getColumnSum = (
  data: Record<string, unknown>[],
  columnKey: string
): number => {
  if (!Array.isArray(data) || data.length === 0) return 0.0;
  const total = data.reduce((sum, row) => {
    const value = parseFloat(String(row[columnKey])) || 0;
    return sum + value;
  }, 0);
  return total;
};

export function formatString(str: string | null | undefined): string {
  if (str) {
    return str
      .split("_") // Split the string by underscores
      .map(
        (word: string) =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() // Capitalize first letter, lowercase the rest
      )
      .join(" "); // Join the words with spaces
  }
  return "";
}

export function formatStrings(
  str: string,
  format: string = "capitalize"
): string | number | null {
  if (!str) {
    return null; // Return null for invalid input
  }

  // Remove underscores and normalize spacing
  str = str.replace(/_/g, " ").trim();

  // Utility function to check if a string is a valid Roman numeral
  const isRomanNumeral = (input: string): boolean => {
    const romanRegex =
      /^(?=[MDCLXVI])M*(C[MD]|D?C{0,3})(X[CL]|L?X{0,3})(I[XV]|V?I{0,3})$/i;
    return romanRegex.test(input);
  };

  // Utility function to convert Roman numerals to numeric values
  const romanToInt = (roman: string): number => {
    const romanMap: Record<string, number> = {
      I: 1,
      V: 5,
      X: 10,
      L: 50,
      C: 100,
      D: 500,
      M: 1000,
    };
    let total = 0;
    let prevValue = 0;

    for (let i = roman.length - 1; i >= 0; i--) {
      const current = romanMap[roman[i].toUpperCase()];
      if (current < prevValue) {
        total -= current; // Subtract if smaller than the previous value (e.g., IV = 4)
      } else {
        total += current; // Add otherwise
      }
      prevValue = current;
    }

    return total;
  };

  // Handle the specified format
  switch (format.toLowerCase()) {
    case "uppercase":
      return str.toUpperCase();
    case "lowercase":
      return str.toLowerCase();
    case "capitalize":
      return str
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    case "roman":
      if (isRomanNumeral(str)) {
        return romanToInt(str);
      }
      return `Invalid Roman numeral: ${str}`;
    default:
      return `Unsupported format: ${format}`;
  }
}

export const formatDate = (
  date: string | number | Date | Dayjs | null | undefined,
  format: string
): string | null => {
  if (typeof date === "number") {
    const excelEpoch = 25569;
    const msPerDay = 86400 * 1000;

    const jsDate = new Date((date - excelEpoch) * msPerDay);

    return dayjs(jsDate).format(format);
  }

  if (typeof date === "string") {
    return dayjs(date).format(format);
  }

  if (date instanceof Date) {
    return dayjs(date).format(format);
  }

  const parsedDate = dayjs(date, "DD/MM/YYYY HH:mm:ss", true);

  if (parsedDate.isValid()) {
    return parsedDate.format(format);
  }

  return null;
};

export function formatTime(time: string) {
  return dayjs(time, "HH:mm").format("h:mm A"); // Converts 24-hour format to 12-hour with AM/PM
}

export function formatNumbers(num: string | number | null | undefined) {
  if (!isNaN(Number(num))) {
    return Number(num).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return "0.00";
}

export function formatNumber(num: string | number | null | undefined) {
  return formatNumbers(num);
}

export function resetPagination(filterConfig: Record<string, unknown>) {
  return {
    ...filterConfig,
    page: 1,
  };
}
